import { useEffect, useState, useRef } from 'react';
import { format, parseISO, startOfDay, endOfDay, isWithinInterval } from 'date-fns';
import { fetchPmsTasks, fetchPmsProjects } from '@/services/pmsApi';
import type { CalendarVisualItem } from '@/components/calendar/views/WeekTimeGrid';

type DateRange = { start: Date; end: Date };
type Options = { enabled?: boolean; includeTasks?: boolean; includeProjects?: boolean };

function toVisualItem(
  id: string,
  kind: CalendarVisualItem['kind'],
  title: string,
  dateStr: string | null | undefined,
): CalendarVisualItem | null {
  if (!dateStr) return null;
  try {
    const day = startOfDay(parseISO(dateStr));
    const dateKey = format(day, 'yyyy-MM-dd');
    const end = endOfDay(day);
    return { id, agendaId: id, kind, title, startsAt: day.toISOString(), endsAt: end.toISOString(), allDay: true, dateKey };
  } catch {
    return null;
  }
}

function inRange(dateStr: string | null | undefined, range: DateRange): boolean {
  if (!dateStr) return false;
  try { return isWithinInterval(parseISO(dateStr), range); } catch { return false; }
}

export function usePmsCalendarItems(range: DateRange, options: Options = {}): CalendarVisualItem[] {
  const [items, setItems] = useState<CalendarVisualItem[]>([]);
  const enabled = options.enabled ?? true;
  const includeTasks = options.includeTasks ?? true;
  const includeProjects = options.includeProjects ?? true;
  const rangeKey = `${range.start.toISOString()}|${range.end.toISOString()}`;
  const lastKey = useRef<string | null>(null);
  const cacheRef = useRef<Map<string, CalendarVisualItem[]>>(new Map());

  useEffect(() => {
    const cacheKey = `${rangeKey}|t:${includeTasks ? 1 : 0}|p:${includeProjects ? 1 : 0}`;
    if (!enabled) { setItems([]); lastKey.current = cacheKey; return; }

    const cached = cacheRef.current.get(cacheKey);
    if (cached) { setItems(cached); lastKey.current = cacheKey; return; }
    if (lastKey.current === cacheKey) return;
    lastKey.current = cacheKey;

    let cancelled = false;

    const load = async () => {
      try {
        const from = format(range.start, 'yyyy-MM-dd');
        const to = format(range.end, 'yyyy-MM-dd');
        const [tasks, projects] = await Promise.all([
          includeTasks ? fetchPmsTasks({ dueDateFrom: from, dueDateTo: to }) : Promise.resolve([]),
          includeProjects ? fetchPmsProjects({ from, to }) : Promise.resolve([]),
        ]);
        if (cancelled) return;

        const result: CalendarVisualItem[] = [];
        for (const task of tasks) {
          const dateStr = task.dueDate ?? task.startDate ?? task.endDate;
          if (!inRange(dateStr, range)) continue;
          const item = toVisualItem(`pms-task-${task.id}`, 'pms-task', task.name, dateStr);
          if (item) result.push(item);
        }
        for (const project of projects) {
          const dateStr = project.endDate ?? project.startDate;
          if (!inRange(dateStr, range)) continue;
          const item = toVisualItem(`pms-project-${project.id}`, 'pms-project', `📁 ${project.name}`, dateStr);
          if (item) result.push(item);
        }

        cacheRef.current.set(cacheKey, result);
        setItems(result);
      } catch {
        // Non-fatal — PMS may be unavailable; calendar still works
      }
    };

    void load();
    return () => { cancelled = true; };
  }, [enabled, includeProjects, includeTasks, rangeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return items;
}
